/**
 * MODULE: _shared/frontend-shell/src/components/assistant/ApprovalModal.tsx
 * PURPOSE: Flowbite modal for destructive assistant tools when SSE emits `approval_required`.
 *
 * DEPENDENCIES:
 *   - flowbite-react: Modal, Button, Spinner
 *   - @heroicons/react: ExclamationTriangleIcon
 *
 * PUBLIC API:
 *   - ApprovalModal, ApprovalModalProps, ApprovalModalBusy
 *
 * SIDE EFFECTS:
 *   - Invokes `onConfirm` / `onCancel` (parent calls gateway approve API).
 *
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: 124 / 200 lines
 * RECENT: 2026-04-19 [2.2.553] — **`data-testid`** hooks for E2E; root **`CHANGELOG` [2.2.553]**.
 */

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'flowbite-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export type ApprovalModalBusy = 'approve' | 'deny' | null;

export interface ApprovalModalProps {
  show: boolean;
  toolName: string;
  toolArgs: Record<string, unknown>;
  message: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  busyAction?: ApprovalModalBusy;
  errorMessage?: string | null;
}

function summarizeArgs(args: Record<string, unknown>): Array<{ key: string; value: string }> {
  return Object.entries(args).map(([key, value]) => ({
    key,
    value:
      value === null || value === undefined
        ? '—'
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value),
  }));
}

export function ApprovalModal({
  show,
  toolName,
  toolArgs,
  message,
  onConfirm,
  onCancel,
  busyAction = null,
  errorMessage = null,
}: ApprovalModalProps) {
  const disabled = busyAction !== null;
  const rows = summarizeArgs(toolArgs ?? {});

  return (
    <Modal
      show={show}
      onClose={() => {
        if (!disabled) void onCancel();
      }}
      dismissible={!disabled}
      size="lg"
    >
      <ModalHeader>
        <span className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
          <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          Confirmation required
        </span>
      </ModalHeader>
      <ModalBody>
        <div data-testid="approval-prompt">
        <p className="text-sm text-gray-800 dark:text-gray-200" data-testid="approval-prompt-desc">
          {message}
        </p>
        <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
          Tool:{' '}
          <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-900 dark:bg-gray-800 dark:text-gray-100">
            {toolName}
          </code>
        </p>
        {rows.length > 0 ? (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800/60">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Parameters
            </p>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
              {rows.map(({ key, value }) => (
                <li key={key} className="flex gap-2 break-all">
                  <span className="shrink-0 font-medium text-gray-700 dark:text-gray-300">{key}:</span>
                  <span className="text-gray-600 dark:text-gray-400">{value}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {errorMessage ? (
          <p className="mt-3 text-sm text-red-700 dark:text-red-300" role="alert">
            {errorMessage}
          </p>
        ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="failure"
          data-testid="approval-cancel-btn"
          disabled={disabled}
          onClick={() => void onCancel()}
        >
          {busyAction === 'deny' ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="sm" />
              Cancelling…
            </span>
          ) : (
            'Cancel'
          )}
        </Button>
        <Button color="blue" data-testid="approval-confirm-btn" disabled={disabled} onClick={() => void onConfirm()}>
          {busyAction === 'approve' ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="sm" />
              Confirming…
            </span>
          ) : (
            'Confirm'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
